import { Link } from 'react-router-dom';
import { CalendarIcon, ArrowRightIcon, ClockIcon } from '@heroicons/react/24/outline';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useBlogFeaturedImage, useBlogCategories } from '@/hooks/useBlog';
import { blogService, WordPressPost } from '@/services/blogService';

interface FeaturedPostCardProps {
  post: WordPressPost;
}

const FeaturedPostCard = ({ post }: FeaturedPostCardProps) => {
  const { data: featuredImageUrl } = useBlogFeaturedImage(post);
  const { data: categories } = useBlogCategories();
  
  // Add defensive checks for post and its properties
  const postCategories = categories?.filter(cat => 
    post?.categories && Array.isArray(post.categories) && post.categories.includes(cat.id)
  ) || [];
  
  const contentText = post?.content?.rendered || '';
  const readingTime = contentText ? Math.ceil(blogService.stripHtml(contentText).split(' ').length / 200) : 1;

  return (
    <Card className="md:flex overflow-hidden">
      <Link to={`/blog/${post.slug}`} className="md:w-1/2 block cursor-pointer">
        <img
          src={featuredImageUrl || "/api/placeholder/400/250"}
          alt={post?.title?.rendered ? blogService.stripHtml(post.title.rendered) : ''}
          className="w-full h-64 md:h-full object-cover no-watercolor"
        />
      </Link>
      <div className="md:w-1/2 p-6 flex flex-col justify-between">
        <div>
          {postCategories.length > 0 && (
            <Badge className="mb-4">{postCategories[0].name}</Badge>
          )}
          <h3 className="text-2xl font-bold text-foreground mb-4">
            {post?.title?.rendered ? blogService.stripHtml(post.title.rendered) : 'No Title'}
          </h3>
          <p className="text-muted-foreground mb-6">
            {post?.excerpt?.rendered ? blogService.stripHtml(post.excerpt.rendered) : ''}
          </p>
        </div>
        <div>
          <div className="flex items-center text-sm text-muted-foreground mb-4">
            <CalendarIcon className="w-4 h-4 mr-2" />
            <span className="mr-4">{post?.date ? new Date(post.date).toLocaleDateString() : ''}</span>
            <ClockIcon className="w-4 h-4 mr-2" />
            <span>{readingTime} min read</span>
          </div>
          <Button asChild>
            <Link to={`/blog/${post.slug}`}>
              Read Article <ArrowRightIcon className="ml-2 w-4 h-4" />
            </Link>
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default FeaturedPostCard;